import { useState, useEffect } from 'react';
import { FileText, Download, Eye, Search, Calendar, Mail, Check, Loader2 } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Invoices = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(null);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/auth/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Transform orders to invoices
      const orders = response.data.orders || [];
      const invoiceData = orders.map(order => ({
        id: order.id,
        invoice_number: `INV-${order.order_number?.replace('ORD-', '') || order.id.slice(0, 8).toUpperCase()}`,
        order_number: order.order_number,
        date: order.timestamp,
        amount: order.total,
        status: order.payment_status || 'paid',
        items: order.items || []
      }));
      setInvoices(invoiceData);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async (invoice) => {
    if (!user?.email) {
      toast.error('No email address found');
      return;
    }

    setSendingEmail(invoice.id);
    
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${API}/invoices/send-email`,
        {
          order_id: invoice.id,
          email: user.email
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success(`Invoice sent to ${user.email}`);
    } catch (error) {
      console.error('Error sending invoice:', error);
      toast.error('Failed to send invoice email');
    } finally {
      setSendingEmail(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30';
      case 'pending': return 'bg-[#F97316]/20 text-[#F97316] border-[#F97316]/30';
      case 'overdue': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-[#3B82F6]/20 text-[#3B82F6] border-[#3B82F6]/30';
    }
  };

  if (loading) {
    return (
      <DashboardLayout currentPage="/dashboard/invoices">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-[#3B82F6]/30 border-t-[#3B82F6] rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout currentPage="/dashboard/invoices">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)] mb-2">Invoices</h1>
        <p className="text-[var(--text-muted)]">View and download your invoices</p>
      </div>

      {invoices.length === 0 ? (
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl p-12 text-center">
          <div className="w-20 h-20 bg-[var(--bg-elevated)] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FileText size={40} className="text-[var(--text-muted)]" />
          </div>
          <h3 className="text-[var(--text-primary)] font-semibold mb-2">No invoices yet</h3>
          <p className="text-[var(--text-muted)]">Your invoices will appear here after your first purchase</p>
        </div>
      ) : (
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--border)]">
                  <th className="text-left text-[var(--text-muted)] text-sm font-medium px-6 py-4">Invoice</th>
                  <th className="text-left text-[var(--text-muted)] text-sm font-medium px-6 py-4">Order</th>
                  <th className="text-left text-[var(--text-muted)] text-sm font-medium px-6 py-4">Date</th>
                  <th className="text-left text-[var(--text-muted)] text-sm font-medium px-6 py-4">Amount</th>
                  <th className="text-left text-[var(--text-muted)] text-sm font-medium px-6 py-4">Status</th>
                  <th className="text-left text-[var(--text-muted)] text-sm font-medium px-6 py-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="border-b border-[var(--border)] hover:bg-[var(--bg-elevated)] transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#8B5CF6]/10 rounded-lg flex items-center justify-center border border-[#8B5CF6]/30">
                          <FileText size={18} className="text-[#8B5CF6]" />
                        </div>
                        <span className="text-[var(--text-primary)] font-medium">{invoice.invoice_number}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-[var(--text-muted)] text-sm">{invoice.order_number}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-[var(--text-muted)] text-sm">
                        <Calendar size={14} />
                        {new Date(invoice.date).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-[var(--text-primary)] font-semibold">${invoice.amount?.toFixed(2)}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-lg text-xs font-semibold border capitalize ${getStatusColor(invoice.status)}`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button 
                          className="p-2 text-[var(--text-muted)] hover:text-[#3B82F6] transition-colors rounded-lg hover:bg-[var(--bg-dark)]"
                          title="View Invoice"
                        >
                          <Eye size={18} />
                        </button>
                        <button 
                          className="p-2 text-[var(--text-muted)] hover:text-[#10B981] transition-colors rounded-lg hover:bg-[var(--bg-dark)]"
                          title="Download Invoice"
                        >
                          <Download size={18} />
                        </button>
                        <button 
                          onClick={() => handleSendEmail(invoice)}
                          disabled={sendingEmail === invoice.id}
                          className="p-2 text-[var(--text-muted)] hover:text-[#8B5CF6] transition-colors rounded-lg hover:bg-[var(--bg-dark)] disabled:opacity-50"
                          title="Send to Email"
                        >
                          {sendingEmail === invoice.id ? (
                            <Loader2 size={18} className="animate-spin" />
                          ) : (
                            <Mail size={18} />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default Invoices;
